/**
 * Los contadores de alarmas (`GET /api/monitor/alarms/summary`), compartidos
 * por la pastilla del menú, la pestaña Alarmas de Monitor y el Dashboard.
 *
 * El menú se monta dos veces (escritorio y cajón de móvil), así que cada
 * componente no puede tener su propio temporizador: un almacén de módulo con
 * UNA petición en vuelo y UN `setInterval` de 60 s, que arranca con el primer
 * suscriptor y para con el último. Con la pestaña oculta no se pregunta, y al
 * volver a verla se pregunta en el acto. Mismo patrón que
 * `useNotificationChannels.ts`.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { getAlarmSummary, type AlarmSummary } from '@/services/api';

const POLL_MS = 60_000;

interface SummaryState {
  summary: AlarmSummary | null;
  error: string;
}

let state: SummaryState = { summary: null, error: '' };
let inflight: Promise<AlarmSummary> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function setState(next: Partial<SummaryState>) {
  state = { ...state, ...next };
  listeners.forEach((notify) => notify());
}

/** Vuelve a pedir los contadores (tras cualquier acción sobre una alarma). */
export function reloadAlarmSummary(): Promise<AlarmSummary> {
  if (!inflight) {
    inflight = getAlarmSummary().then(
      (summary) => {
        inflight = null;
        setState({ summary, error: '' });
        return summary;
      },
      (err: unknown) => {
        inflight = null;
        // Se conserva el último bueno: la pastilla no se apaga por un fallo suelto.
        setState({ error: err instanceof Error && err.message ? err.message : 'No se pudieron cargar las alarmas' });
        throw err;
      },
    );
  }
  return inflight;
}

const reloadQuietly = () => {
  reloadAlarmSummary().catch(() => { /* el fallo queda en `error` */ });
};

const onVisibility = () => {
  if (!document.hidden) reloadQuietly();
};

function start() {
  if (timer) return;
  timer = setInterval(() => {
    if (!document.hidden) reloadQuietly();
  }, POLL_MS);
  document.addEventListener('visibilitychange', onVisibility);
}

function stop() {
  if (timer) clearInterval(timer);
  timer = null;
  document.removeEventListener('visibilitychange', onVisibility);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

const snapshot = () => state;

/** Contadores (`null` hasta la primera respuesta), último error y `reload`. */
export function useAlarmSummary(): { summary: AlarmSummary | null; error: string; reload: () => void } {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!state.summary && !inflight) reloadQuietly();
  }, []);
  return { summary: current.summary, error: current.error, reload: reloadQuietly };
}
